import { api } from '@/lib/api';
import type {
  CompanyAnnualRevenue,
  CompanyCertification,
  CompanyInterventionZone,
  CompanyProfile,
  CpvCodeOption,
  CreateBlockInput,
  CreateCertificationInput,
  CreateInterventionZoneInput,
  CreateOrUpdateProfileInput,
  EffectiveCpvResult,
  OnboardingStatus,
  KnowledgeBlock,
  ListBlocksFilters,
  PermanentFile,
  PieceType,
  PresignPieceUploadInput,
  PresignPieceUploadResult,
  ProfileResponse,
  TemplateVariableDescriptor,
  UpdateAggregatedReferencesInput,
  UpdateBlockInput,
  UpdateCertificationInput,
  UpdateCommercialStrategyInput,
  UpdateInterventionZoneInput,
  UpdatePieceMetadataInput,
  UpsertAnnualRevenueInput,
} from '../types/dossier-company.types';

const BASE = '/dossier-company';

/**
 * Sprint 5 v3 — Client API du Dossier company (3 briques).
 */
export const dossierCompanyService = {
  // ─────────────────── Pièces ───────────────────
  async listPieces(filters?: { type?: PieceType }): Promise<PermanentFile[]> {
    const { data } = await api.get<PermanentFile[]>(`${BASE}/pieces`, { params: filters });
    return data;
  },

  async presignPiece(input: PresignPieceUploadInput): Promise<PresignPieceUploadResult> {
    const { data } = await api.post<PresignPieceUploadResult>(`${BASE}/pieces/presign`, input);
    return data;
  },

  async uploadToMinio(uploadUrl: string, file: File): Promise<void> {
    // PUT direct sur MinIO presigned URL (pas via api/v1)
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!response.ok) {
      throw new Error(`Upload MinIO failed: ${response.status} ${response.statusText}`);
    }
  },

  async confirmPiece(pieceId: number): Promise<void> {
    await api.post(`${BASE}/pieces/${pieceId}/confirm`);
  },

  async getDownloadUrl(pieceId: number): Promise<string> {
    const { data } = await api.get<{ url: string }>(`${BASE}/pieces/${pieceId}/download`);
    return data.url;
  },

  async updatePieceMetadata(pieceId: number, input: UpdatePieceMetadataInput): Promise<PermanentFile> {
    const { data } = await api.patch<PermanentFile>(`${BASE}/pieces/${pieceId}`, input);
    return data;
  },

  async deletePiece(pieceId: number): Promise<void> {
    await api.delete(`${BASE}/pieces/${pieceId}`);
  },

  // ─────────────────── Blocs ───────────────────
  async listBlocks(filters?: ListBlocksFilters): Promise<KnowledgeBlock[]> {
    const { data } = await api.get<KnowledgeBlock[]>(`${BASE}/blocks`, { params: filters });
    return data;
  },

  async getBlock(blockId: number): Promise<KnowledgeBlock> {
    const { data } = await api.get<KnowledgeBlock>(`${BASE}/blocks/${blockId}`);
    return data;
  },

  async createBlock(input: CreateBlockInput): Promise<KnowledgeBlock> {
    const { data } = await api.post<KnowledgeBlock>(`${BASE}/blocks`, input);
    return data;
  },

  async updateBlock(blockId: number, input: UpdateBlockInput): Promise<KnowledgeBlock> {
    const { data } = await api.patch<KnowledgeBlock>(`${BASE}/blocks/${blockId}`, input);
    return data;
  },

  async deleteBlock(blockId: number): Promise<void> {
    await api.delete(`${BASE}/blocks/${blockId}`);
  },

  // ─────────────────── Profil ───────────────────
  async getProfile(): Promise<ProfileResponse> {
    const { data } = await api.get<ProfileResponse>(`${BASE}/profile`);
    return data;
  },

  async createProfile(input: CreateOrUpdateProfileInput): Promise<CompanyProfile> {
    const { data } = await api.post<CompanyProfile>(`${BASE}/profile`, input);
    return data;
  },

  async updateProfile(input: CreateOrUpdateProfileInput): Promise<CompanyProfile> {
    const { data } = await api.patch<CompanyProfile>(`${BASE}/profile`, input);
    return data;
  },

  async refreshSirene(): Promise<{ status: string }> {
    const { data } = await api.post<{ status: string }>(`${BASE}/profile/refresh-sirene`);
    return data;
  },

  // ─────────────────── CA ───────────────────
  async listRevenues(): Promise<CompanyAnnualRevenue[]> {
    const { data } = await api.get<CompanyAnnualRevenue[]>(`${BASE}/profile/revenues`);
    return data;
  },

  async upsertRevenue(input: UpsertAnnualRevenueInput): Promise<CompanyAnnualRevenue> {
    const { data } = await api.post<CompanyAnnualRevenue>(`${BASE}/profile/revenues`, input);
    return data;
  },

  async deleteRevenue(revenueId: number): Promise<void> {
    await api.delete(`${BASE}/profile/revenues/${revenueId}`);
  },

  // ─────────────────── Certifs ───────────────────
  async listCertifications(): Promise<CompanyCertification[]> {
    const { data } = await api.get<CompanyCertification[]>(`${BASE}/profile/certifications`);
    return data;
  },

  async createCertification(input: CreateCertificationInput): Promise<CompanyCertification> {
    const { data } = await api.post<CompanyCertification>(`${BASE}/profile/certifications`, input);
    return data;
  },

  async updateCertification(certId: number, input: UpdateCertificationInput): Promise<CompanyCertification> {
    const { data } = await api.patch<CompanyCertification>(
      `${BASE}/profile/certifications/${certId}`,
      input,
    );
    return data;
  },

  async archiveCertification(certId: number): Promise<void> {
    await api.delete(`${BASE}/profile/certifications/${certId}`);
  },

  // ─────────────────── Variables ───────────────────
  async listAvailableVariables(): Promise<TemplateVariableDescriptor[]> {
    const { data } = await api.get<TemplateVariableDescriptor[]>(`${BASE}/template-variables`);
    return data;
  },

  async resolveVariables(opportunityId?: number): Promise<Record<string, string>> {
    const { data } = await api.get<Record<string, string>>(
      `${BASE}/template-variables/resolve`,
      { params: opportunityId ? { opportunityId } : {} },
    );
    return data;
  },

  // ─────────────────── Sprint 5b.1 v2 — Effective CPV ───────────────────
  async getEffectiveCpv(): Promise<EffectiveCpvResult> {
    const { data } = await api.get<EffectiveCpvResult>(`${BASE}/effective-cpv`);
    return data;
  },

  async listCpvCodes(): Promise<CpvCodeOption[]> {
    const { data } = await api.get<CpvCodeOption[]>(`${BASE}/cpv-codes`);
    return data;
  },

  // ─────────────────── Sprint 5b.1 v2 — Zones d'intervention ───────────────────
  async listInterventionZones(includeArchived = false): Promise<CompanyInterventionZone[]> {
    const { data } = await api.get<CompanyInterventionZone[]>(
      `${BASE}/intervention-zones`,
      { params: includeArchived ? { includeArchived: true } : {} },
    );
    return data;
  },

  async createInterventionZone(
    input: CreateInterventionZoneInput,
  ): Promise<CompanyInterventionZone> {
    const { data } = await api.post<CompanyInterventionZone>(
      `${BASE}/intervention-zones`,
      input,
    );
    return data;
  },

  async updateInterventionZone(
    zoneId: number,
    input: UpdateInterventionZoneInput,
  ): Promise<CompanyInterventionZone> {
    const { data } = await api.patch<CompanyInterventionZone>(
      `${BASE}/intervention-zones/${zoneId}`,
      input,
    );
    return data;
  },

  async archiveInterventionZone(zoneId: number): Promise<void> {
    await api.delete(`${BASE}/intervention-zones/${zoneId}`);
  },

  // ─────────────────── Sprint 5b.1 v2 — Stratégie commerciale ───────────────────
  async updateCommercialStrategy(
    input: UpdateCommercialStrategyInput,
  ): Promise<CompanyProfile> {
    const { data } = await api.patch<CompanyProfile>(
      `${BASE}/commercial-strategy`,
      input,
    );
    return data;
  },

  async updateAggregatedReferences(
    input: UpdateAggregatedReferencesInput,
  ): Promise<CompanyProfile> {
    const { data } = await api.patch<CompanyProfile>(
      `${BASE}/aggregated-references`,
      input,
    );
    return data;
  },

  // ─────────────────── Sprint 5b.1 v2 — Wizard onboarding ───────────────────
  async dismissOnboarding(): Promise<{ status: string }> {
    const { data } = await api.post<{ status: string }>(`${BASE}/onboarding/dismiss`, {});
    return data;
  },

  async getOnboardingStatus(): Promise<OnboardingStatus> {
    const { data } = await api.get<OnboardingStatus>(`${BASE}/onboarding/status`);
    return data;
  },
};
